import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { getAuditLog } from '../services/api';
import { format } from 'date-fns';

const FIELD_LABELS = {
  status:        'Status',
  priority:      'Priority',
  assigned_to:   'Assigned To',
  escalated:     'Escalated',
  title:         'Title',
  description:   'Description',
};

export default function AuditLogPage() {
  const [audit, setAudit] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await getAuditLog(params);
      setAudit(res.data.audit || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAudit();
  };

  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setPage(1);
  };

  const isUUID = (val) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val));

  const formatValue = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    if (val === 'true') return 'Yes';
    if (val === 'false') return 'No';
    const str = String(val);
    if (isUUID(str)) return str.slice(0, 8) + '…';
    return str.replace(/_/g, ' ');
  };

  return (
    <AppLayout pageTitle="Audit Log">
      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
        <form onSubmit={handleFilter} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 4 }}>
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{
                padding: '8px 10px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-main)', fontSize: 13,
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 4 }}>
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{
                padding: '8px 10px',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 8, color: 'var(--text-main)', fontSize: 13,
              }}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm">Apply Filter</button>
          {(fromDate || toDate) && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>
              Clear
            </button>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
            {total} total entries
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Change History</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div className="flex-center" style={{ height: 200 }}>
              <div className="spinner" style={{ width: 32, height: 32 }} />
            </div>
          ) : audit.length === 0 ? (
            <div className="empty-state" style={{ padding: 60 }}>
              <div className="empty-icon">🔍</div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-main)', marginBottom: 8 }}>
                No audit records found
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>
                {fromDate || toDate ? 'No changes found in this date range.' : 'No ticket changes have been logged yet.'}
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Ticket #</th>
                  <th>Title</th>
                  <th>Changed By</th>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {audit.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-sub)' }}>
                      {format(new Date(entry.changed_at), 'dd MMM yyyy, HH:mm')}
                    </td>
                    <td>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 12,
                        color: 'var(--primary)', fontWeight: 600,
                      }}>
                        {entry.ticket_number || '—'}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      <span style={{
                        display: 'block', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontSize: 13, color: 'var(--text-main)',
                      }}>
                        {entry.ticket_title || '—'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-main)' }}>
                        {entry.changed_by_name || 'System'}
                      </div>
                      {entry.changed_by_role && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                          {entry.changed_by_role}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge" style={{ background: 'var(--bg-hover)', color: 'var(--text-sub)' }}>
                        {FIELD_LABELS[entry.field_changed] || entry.field_changed}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 12, color: 'var(--text-sub)',
                        textDecoration: 'line-through', textTransform: 'capitalize',
                      }}>
                        {formatValue(entry.old_value)}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 12, fontWeight: 600,
                        color: 'var(--text-main)', textTransform: 'capitalize',
                      }}>
                        {formatValue(entry.new_value)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', borderTop: '1px solid var(--border-light)',
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
